/**
 * globalFacilityStore.ts
 *
 * Holds the user's current "global Facility" override. When set to a non-empty
 * list of facilityIds, every facility-aware page locks its Facility filter to
 * those values without destroying the page's own saved preset. When empty
 * ("All Facilities"), pages fall back to their own filter values.
 *
 * Persistence lives on the backend via global-facility.service; this store is
 * not mirrored to localStorage so the value follows the user across devices.
 */
import { create } from 'zustand';
import { fetchGlobalFacility, saveGlobalFacility } from '@/services/global-facility.service';
import { queryClient } from '@/lib/queryClient';

interface GlobalFacilityState {
  selectedFacilityIds: number[];
  hydrated: boolean;
  hydrate: () => Promise<void>;
  setSelected: (ids: number[]) => Promise<void>;
  reset: () => void;
}

/** Normalize a list of facilityIds: keep positive integers, dedupe, sort. */
function normalizeIds(ids: number[]): number[] {
  const seen = new Set<number>();
  for (const raw of ids) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) seen.add(n);
  }
  return Array.from(seen).sort((a, b) => a - b);
}

function sameIds(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export const useGlobalFacilityStore = create<GlobalFacilityState>((set, get) => ({
  selectedFacilityIds: [],
  hydrated: false,

  hydrate: async () => {
    try {
      const ids = await fetchGlobalFacility();
      set({ selectedFacilityIds: normalizeIds(ids), hydrated: true });
    } catch {
      // Hydration failure is non-fatal — treat as "no override".
      set({ hydrated: true });
    }
  },

  setSelected: async (ids) => {
    const next = normalizeIds(ids);
    const prev = get().selectedFacilityIds;
    if (sameIds(prev, next)) return;
    set({ selectedFacilityIds: next });
    try {
      await saveGlobalFacility(next);
    } catch (err) {
      set({ selectedFacilityIds: prev });
      throw err;
    }
    // Force every list/detail query to refetch under the new facility scope.
    queryClient.invalidateQueries();
  },

  reset: () => set({ selectedFacilityIds: [], hydrated: false }),
}));
